import React from 'react';
import {tokens} from '../../themes';
import type {
  HTMLAttributes,
  SpacingNumber,
  SpringTooling,
} from '../__SharedTypes';

import {setLocalTokens, DividerView} from './Divider.styles';

export type DividerProps = {
  /**
   * Determines the color of the `Divider`. The number is associated with the border token (i.e. 1 for $border1).
   */
  elevation?: 1 | 2 | 3;
  /**
   * Orientation of the `Divider`. The height of a `vertical` `Divider` is controlled by its parent.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Adds spacing before and after the `Divider`. Ideally any spacing would be provided by `Stack` components,
   * but this prop provides another option for any edge cases where that doesn't fulfill the requirements.
   */
  spacing?: SpacingNumber;
} & SpringTooling &
  HTMLAttributes;

const DividerWrapper = ({
  children,
  orientation,
}: {
  children: React.ReactNode;
  orientation: DividerProps['orientation'];
}) => {
  // We are wrapping horizontal Dividers in a `span` so they still take up space in a flex parent with overflow set.
  return orientation === 'horizontal' ? <span>{children}</span> : children;
};

/**
 * @component
 */
export const Divider = React.forwardRef<HTMLHRElement, DividerProps>(
  (
    {
      children: _children, // grab and toss, we don't want DividerView to accept children.
      elevation = 1,
      orientation = 'horizontal',
      spacing = 0,
      ...props
    }: DividerProps,
    ref
  ) => {
    const margin = tokens.space.getValue(spacing);
    const style = {...props.style, ...setLocalTokens({margin})} as const;

    return (
      <DividerWrapper orientation={orientation}>
        <DividerView
          aria-orientation={orientation}
          elevation={elevation}
          orientation={orientation}
          role="separator"
          {...props}
          ref={ref}
          style={style}
        />
      </DividerWrapper>
    );
  }
);

Divider.displayName = 'Divider';
Divider.toString = DividerView.toString;

export default Divider;
